"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.transformOperation = exports.validateTransform = void 0;
const csvtojson_1 = __importDefault(require("csvtojson"));
const lodash_1 = __importDefault(require("lodash"));
const common_1 = require("@finley-technologies/common");
const consolidateErrors_1 = require("./consolidateErrors");
const errorUtils_1 = require("./errorUtils");
const helpers_1 = require("./helpers");
const typeguards_1 = require("./typeguards");
const utils_1 = require("./utils");
// excel/csv have a header row and are 1 based arrays, so adjust for our 0 based rowData
const STANDARD_ROW_INDEX_OFFSET = 2;
const consolidateDuplicateValuesErrors = (unknownErrors) => {
    return (0, consolidateErrors_1.consolidateErrors)(unknownErrors, (error) => error.field, // Group by field
    (error) => ({
        firstRow: String(error.firstRow),
        duplicateRow: String(error.duplicateRow),
        value: error.value,
    }), // Map to row data
    (field) => `Duplicate values found for field [${field}]`);
};
const consolidateMissingDataErrors = (unknownErrors) => {
    return (0, consolidateErrors_1.consolidateErrors)(unknownErrors, (error) => error.field, // Group by field
    (error) => ({
        row: String(error.row),
        value: error.value,
    }), // Map to row data
    (field) => `Tape missing value for field [${field}]`);
};
const consolidateToStructuredErrors = (errors) => {
    const missingDataErrors = errors.filter((error) => error.type === 'joinMissingDataError');
    const duplicateValuesErrors = errors.filter((error) => error.type === 'joinDuplicateDataError');
    // we want to consolidate the errors if there are too many of those.
    return [
        ...consolidateMissingDataErrors(missingDataErrors),
        ...consolidateDuplicateValuesErrors(duplicateValuesErrors),
    ];
};
const checkJoinTransformOp = (transformation) => {
    const errors = [];
    const srcJoinOnBaseField = lodash_1.default.find(transformation.srcDataTape.baseFields, (bf) => bf.field === transformation.joinOnField);
    // check that joinOnField exists in srcDataTape basefields
    if (srcJoinOnBaseField === undefined) {
        errors.push({
            type: 'metadataValidation',
            message: `Source DataTape baseFields is missing joinOnField - checkJoinTransformOp: ${transformation.joinOnField}`,
        });
    }
    // check that srcDataTape joinOnField basefield is required
    if (srcJoinOnBaseField !== undefined &&
        !(0, typeguards_1.isRequiredBaseFieldConfig)(srcJoinOnBaseField)) {
        errors.push({
            type: 'metadataValidation',
            message: `Source DataTape joinOnField baseField must be a required field - checkJoinTransformOp: ${transformation.joinOnField}`,
        });
    }
    const joinJoinOnBaseField = lodash_1.default.find(transformation.joinDataTape.baseFields, (bf) => bf.field === transformation.joinOnField);
    // check that joinOnField exists in joinDataTape basefields
    if (joinJoinOnBaseField === undefined) {
        errors.push({
            type: 'metadataValidation',
            message: `Join DataTape baseFields is missing joinOnField - checkJoinTransformOp: ${transformation.joinOnField}`,
        });
    }
    // check that srcDataTape joinOnField basefield is required
    if (joinJoinOnBaseField !== undefined &&
        !(0, typeguards_1.isRequiredBaseFieldConfig)(joinJoinOnBaseField)) {
        errors.push({
            type: 'metadataValidation',
            message: `Join DataTape joinOnField baseField must be a required field - checkJoinTransformOp: ${transformation.joinOnField}`,
        });
    }
    // check that srcDataTape and joinDataTape joinOnField basefield are the same type
    if (srcJoinOnBaseField !== undefined &&
        joinJoinOnBaseField !== undefined &&
        srcJoinOnBaseField?.type !== joinJoinOnBaseField.type) {
        errors.push({
            type: 'metadataValidation',
            message: `Source DataTape and Join DataTape joinOnField baseFields must be the same type - checkJoinTransformOp: ${transformation.joinOnField} ${srcJoinOnBaseField.type} ${joinJoinOnBaseField.type}`,
        });
    }
    // check that all joinFields exist in joinData basefields
    const missingJoinFields = lodash_1.default.filter(transformation.joinFields, (jf) => !lodash_1.default.some(transformation.joinDataTape.baseFields, (bf) => bf.field === jf.field));
    if (missingJoinFields.length !== 0) {
        errors.push({
            type: 'metadataValidation',
            message: `Join DataTape missing joinFields - checkJoinTransformOp: ${missingJoinFields
                .map((jf) => jf.field)
                .join(',')}`,
        });
    }
    const srcFields = transformation.srcDataTape.baseFields.map((bf) => bf.field);
    // remove joinOnField from srcFields
    lodash_1.default.pull(srcFields, transformation.joinOnField);
    // check that all srcFields and joinFields are unique
    const dupeFields = lodash_1.default.chain([
        ...srcFields,
        ...transformation.joinFields.map((jf) => jf.field),
    ])
        .groupBy()
        .pickBy((f) => f.length > 1)
        .keys()
        .value();
    if (dupeFields.length !== 0) {
        errors.push({
            type: 'metadataValidation',
            message: `Duplicate sourceFields and joinFields - checkJoinTransformOp: ${dupeFields.join(',')}`,
        });
    }
    if (!transformation.errorOnMissingJoinRow) {
        // check that all joinFields conform to join dataTape baseField configurations
        const joinBaseFieldsMap = lodash_1.default.keyBy(transformation.joinDataTape.baseFields, 'field');
        transformation.joinFields.forEach((jf) => {
            const baseField = joinBaseFieldsMap[jf.field];
            if (!baseField) {
                (0, common_1.debugLog)('Missing joinField in joinDataTape: available joinBaseFieldsMap:', joinBaseFieldsMap);
                // missing field. Do not process further.
                // we will catch missing fields error later.
                return;
            }
            // check if the baseField is required, if so, the joinField must have a default to
            if ((0, typeguards_1.isRequiredBaseFieldConfig)(baseField) && jf.defaultTo === undefined) {
                errors.push({
                    type: 'metadataValidation',
                    message: `Join BaseField is required, must have defaultTo value for joinField - checkJoinTransformOp: ${jf.field}`,
                });
            }
            // if there is a defaultTo value, it must conform to the baseField configuration
            if (jf.defaultTo !== undefined) {
                // ensure that a boolean has the correct externalBoolean. Only true and false allowed.
                if ((0, typeguards_1.isBooleanBaseFieldConfig)(baseField) &&
                    !['true', 'false'].includes(jf.defaultTo)) {
                    errors.push({
                        type: 'metadataValidation',
                        message: `Must have valid defaultTo value (true or false) for boolean joinField [field:${baseField.field}], [found:${jf.defaultTo}]`,
                    });
                    return;
                }
                try {
                    (0, helpers_1.getSanitizedDataTapeField)(baseField, jf.defaultTo, true);
                }
                catch (err) {
                    if ((0, typeguards_1.isInvalidField)(err)) {
                        errors.push({
                            type: 'metadataValidation',
                            message: `Must have valid defaultTo value for joinField - checkJoinTransformOp: ${jf.field} ${err.expectedType} ${jf.defaultTo}`,
                        });
                    }
                    else {
                        errors.push({
                            type: 'metadataValidation',
                            message: `Unexpected row sanitization error - checkJoinTransformOp: ${jf.field} ${err.message}`,
                        });
                    }
                }
            }
        });
    }
    return errors;
};
const validateTransform = (transformation) => {
    const errors = checkJoinTransformOp(transformation);
    if (errors.length > 0) {
        return {
            isValid: false,
            errors,
        };
    }
    const joinBaseFieldsMap = lodash_1.default.keyBy(transformation.joinDataTape.baseFields, 'field');
    const baseFields = [
        ...transformation.srcDataTape.baseFields,
        ...lodash_1.default.map(transformation.joinFields, (jf) => joinBaseFieldsMap[jf.field]),
    ];
    return {
        isValid: true,
        data: {
            baseFields,
            delimiter: ',',
            sanitizedFieldNames: false,
        },
    };
};
exports.validateTransform = validateTransform;
const checkJoinTransformData = ({ transformation, joinData, }) => {
    const errors = [];
    const field = transformation.joinOnField;
    // Map to track the first occurrence of each value
    const valueMap = new Map();
    joinData.forEach((item, rowIndex) => {
        const value = String(item[field]);
        if (valueMap.has(value)) {
            // If value is already encountered, log an error
            errors.push({
                type: 'joinDuplicateDataError',
                field,
                firstRow: valueMap.get(value) + 1,
                duplicateRow: rowIndex + 1,
                value,
            });
        }
        else {
            // Record the first occurrence of the value
            valueMap.set(value, rowIndex);
        }
    });
    return errors;
};
const performTransform = async ({ dataTapeBuffers, transformation, }) => {
    (0, common_1.finleyAssert)(dataTapeBuffers.length === 2, 'Join:Expected 2 data tape buffers');
    const [srcDataTapeBuffer, joinDataTapeBuffer] = dataTapeBuffers;
    try {
        const srcCsvString = common_1.finleyBuffer.toString(srcDataTapeBuffer);
        const srcDelimiter = transformation.srcDataTape.delimiter;
        const joinCsvString = common_1.finleyBuffer.toString(joinDataTapeBuffer);
        const joinDelimiter = transformation.joinDataTape.delimiter;
        const tapeDefinitionResult = (0, exports.validateTransform)(transformation);
        (0, common_1.finleyAssert)(tapeDefinitionResult.isValid, 'Join:Invalid tape definition');
        (0, common_1.finleyAssert)(tapeDefinitionResult.data, 'Join:Invalid tape definition data');
        // get the outputDataTape early in the process to error out if necessary
        const outputDataTape = tapeDefinitionResult.data;
        const [srcHeaders, joinHeaders] = await Promise.all([
            (0, helpers_1.getHeaders)({
                csvString: srcCsvString,
                delimiter: srcDelimiter,
                sanitizeFieldNames: false, // field names are sanitized previously by sanitize op
            }),
            (0, helpers_1.getHeaders)({
                csvString: joinCsvString,
                delimiter: joinDelimiter,
                sanitizeFieldNames: false, // field names are sanitized previously by sanitize op
            }),
        ]);
        const [srcData, joinData] = await Promise.all([
            (0, csvtojson_1.default)({ headers: srcHeaders, delimiter: srcDelimiter }).fromString(srcCsvString),
            (0, csvtojson_1.default)({
                headers: joinHeaders,
                delimiter: joinDelimiter,
            }).fromString(joinCsvString),
        ]);
        const joinOnField = transformation.joinOnField;
        const errorOnMissingJoinRow = transformation.errorOnMissingJoinRow;
        // ensure the data is joinable
        const joinDataErrors = checkJoinTransformData({
            transformation,
            joinData,
        });
        // set joinOnDefault once for reuse in map
        let joinOnDefault;
        if (!errorOnMissingJoinRow) {
            const defaultedJoinFields = transformation.joinFields.filter(({ defaultTo }) => defaultTo !== undefined);
            const joinBaseFieldsMap = lodash_1.default.keyBy(transformation.joinDataTape.baseFields, 'field');
            const joinFieldsMap = lodash_1.default.keyBy(defaultedJoinFields, 'field');
            joinOnDefault = lodash_1.default.reduce(joinFieldsMap, (acc, jf, prop) => ({
                ...acc,
                [prop]: (0, helpers_1.getSanitizedDataTapeField)(joinBaseFieldsMap[prop], jf.defaultTo, true),
            }), {});
        }
        const srcFields = transformation.srcDataTape.baseFields.map((f) => f.field);
        const joinFields = transformation.joinFields.map((jf) => jf.field);
        const data = srcData.map((src, index) => {
            // check if joinOn matches in src and join data
            let joinMatch = lodash_1.default.find(joinData, (jd) => src[joinOnField] === jd[joinOnField]);
            if (joinMatch === undefined && errorOnMissingJoinRow) {
                joinDataErrors.push({
                    type: 'joinMissingDataError',
                    // excel/csv have a header row and are 1 based arrays, so adjust for our 0 based rowData
                    row: index + STANDARD_ROW_INDEX_OFFSET,
                    value: String(src[joinOnField]),
                    field: joinOnField,
                });
                return {};
            }
            // if a match does not exist, if required with a default, set to that value
            if (joinMatch === undefined && joinOnDefault !== undefined) {
                joinMatch = joinOnDefault;
            }
            return {
                ...lodash_1.default.pick(src, srcFields),
                ...lodash_1.default.pick(joinMatch, joinFields),
            };
        });
        // if any errors exist, entire run is invalid
        if (joinDataErrors.length !== 0) {
            return {
                isValid: false,
                errors: consolidateToStructuredErrors(joinDataErrors),
            };
        }
        const outputBuffer = (0, utils_1.rowDataToCsv)(data, outputDataTape);
        return {
            isValid: true,
            data: {
                outputBuffer,
            },
            supplementals: [],
        };
    }
    catch (error) {
        return (0, errorUtils_1.convertErrorToTapeFailureResult)(error, 'join: ');
    }
};
exports.transformOperation = {
    operation: 'join',
    validateTransform: exports.validateTransform,
    performTransform,
};
//# sourceMappingURL=join-transform.js.map